import {
	ButtonGroup,
	ButtonGroupItem,
} from "@/components/base/button-group/button-group";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { AdminFormI18nPaths } from "../../admin-form-editor-i18n-paths";
import {
	FormRuleConditionLogic,
	type FormRuleConditionLogicValue,
} from "../../types/form-rule-types";

export interface FormRuleConditionLogicToggleProps {
	value: FormRuleConditionLogicValue;
	onChange: (value: FormRuleConditionLogicValue) => void;
	paths: AdminFormI18nPaths;
}

/**
 * Segmented All / Any control for how multiple form-rule conditions combine.
 */
export function FormRuleConditionLogicToggle({
	value,
	onChange,
	paths,
}: FormRuleConditionLogicToggleProps) {
	const { t } = useAppTranslation();

	const hint =
		value === FormRuleConditionLogic.all
			? t(paths.formRules.conditionLogic.allHint)
			: t(paths.formRules.conditionLogic.anyHint);

	return (
		<div className="flex flex-wrap items-center gap-3">
			<span className="text-sm font-medium text-primary">
				{t(paths.formRules.conditionLogic.matchLabel)}
			</span>
			<ButtonGroup
				selectedKeys={new Set([value])}
				onSelectionChange={(keys) => {
					const key = Array.from(keys)[0];
					if (
						key === FormRuleConditionLogic.all ||
						key === FormRuleConditionLogic.any
					) {
						onChange(key);
					}
				}}
				aria-label={t(paths.formRules.conditionLogic.all)}
				size="sm"
			>
				<ButtonGroupItem
					id={FormRuleConditionLogic.all}
					className="selected:bg-brand-solid selected:text-white"
				>
					{t(paths.formRules.conditionLogic.all)}
				</ButtonGroupItem>
				<ButtonGroupItem
					id={FormRuleConditionLogic.any}
					className="selected:bg-brand-solid selected:text-white"
				>
					{t(paths.formRules.conditionLogic.any)}
				</ButtonGroupItem>
			</ButtonGroup>
			<span className="text-xs text-tertiary">{hint}</span>
		</div>
	);
}
